import { GidType, toGid } from "@redotech/redo-model/shopify/gid-utils";
import { getLocalStorageWithExpiry } from "@redotech/redo-web/utils/local-storage-wrapper";
import { PRICING_TEST_TREATMENT_ID_LOCAL_STORAGE_KEY } from "@redotech/redo-web/utils/shared-conf";
import { useCallback, useEffect, useRef } from "react";
import { StorefrontClient } from "../client/storefront";
import { log } from "../monitoring/log";
import { getShopPayProductOnClickEvent } from "./checkout-buttons/on-click-logic";

// Selectors for various wallet payment buttons
const WALLET_SELECTORS = [
  "shop-pay-wallet-button", // Shopify Shop Pay
  "shopify-amazon-pay-button", // Shopify Amazon Pay
  "shopify-paypal-button", // Shopify PayPal
  "shopify-google-pay-button", // Shopify Google Pay
  "button.rebuy-cart__shop-pay-button", // Rebuy Parent Container that holds all Rebuy Buy Now Buttons
];

type AsyncEventHandler = (
  event: React.MouseEvent | React.TouchEvent,
) => Promise<void>;

type UseBuyNowCoverageProps = {
  shouldInterceptBuyNow?: boolean;
  onClickCoverage: AsyncEventHandler | null;
  storefrontClient?: StorefrontClient;
  buyNowRedoProduct: string | null;
  buyNowCustomCss?: string;
};

/**
 * Extracts variant ID and quantity from the product form
 */
export const extractProductFormData = (element?: HTMLElement) => {
  let elementToUse = element;

  if (!elementToUse) {
    elementToUse = document.querySelector(
      WALLET_SELECTORS.join(","),
    ) as HTMLElement;
  }

  if (!elementToUse) {
    log("no wallet button found");
    return { variantId: 0, quantity: 1 };
  }

  const form = elementToUse.closest('form[action="/cart/add"]');
  if (!form) {
    log("Form with action='/cart/add' not found");
    return { variantId: 0, quantity: 1 };
  }

  const variantInput = form.querySelector('[name="id"]') as HTMLInputElement;
  const quantityInput = form.querySelector(
    'input[name="quantity"]',
  ) as HTMLInputElement;
  const urlParams = new URLSearchParams(window.location.search);
  const variantUrlParam = urlParams.get("variant");

  const variantId = variantInput?.value
    ? variantInput.value
    : variantUrlParam || "0";
  const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

  return { variantId, quantity };
};

export const useBuyNowCoverage = ({
  shouldInterceptBuyNow = false,
  onClickCoverage,
  storefrontClient,
  buyNowRedoProduct,
  buyNowCustomCss,
}: UseBuyNowCoverageProps) => {
  const onClickCoverageRef = useRef(onClickCoverage);
  const storefrontClientRef = useRef(storefrontClient);
  const buyNowRedoProductRef = useRef(buyNowRedoProduct);
  useEffect(() => {
    onClickCoverageRef.current = onClickCoverage;
  }, [onClickCoverage]);

  useEffect(() => {
    storefrontClientRef.current = storefrontClient;
  }, [storefrontClient]);

  useEffect(() => {
    buyNowRedoProductRef.current = buyNowRedoProduct;
  }, [buyNowRedoProduct]);

  /**
   * Handles click events on wallet buttons
   */
  const handleWalletClick = useCallback(async (e: Event) => {
    const clickedElement = e.target as HTMLElement;
    const relevantButton = clickedElement.closest(
      WALLET_SELECTORS.join(","),
    ) as HTMLElement | null;

    if (!relevantButton) return;

    const eventToPass = e as unknown as React.MouseEvent | React.TouchEvent;

    // Check if this is a Shop Pay button on a product page
    const isShopPayButton = relevantButton.tagName === "SHOP-PAY-WALLET-BUTTON";
    const pageType = relevantButton.getAttribute("page-type");
    const isShopPayProductButton = isShopPayButton && pageType === "product";

    // Handle Shop Pay product page buttons specially
    if (
      isShopPayProductButton &&
      storefrontClientRef.current &&
      buyNowRedoProductRef.current
    ) {
      const { variantId, quantity } = extractProductFormData(clickedElement);

      if (!variantId && !quantity) return;

      eventToPass.stopPropagation();

      const pricingTestTreatmentId = getLocalStorageWithExpiry(
        PRICING_TEST_TREATMENT_ID_LOCAL_STORAGE_KEY,
      );

      const shopPayHandler = getShopPayProductOnClickEvent({
        storefrontClient: storefrontClientRef.current,
        productVariantId: toGid(GidType.PRODUCT_VARIANT, variantId),
        productVariantQuantity: quantity,
        redoProductVariantId: toGid(
          GidType.PRODUCT_VARIANT,
          buyNowRedoProductRef.current,
        ),
        pricingTestTreatmentId: pricingTestTreatmentId ?? undefined,
      });

      await shopPayHandler();
    }
    // Handle other wallet buttons with the provided coverage handler
    else if (onClickCoverageRef.current && !isShopPayProductButton) {
      await onClickCoverageRef.current(eventToPass);
    }
  }, []);

  useEffect(() => {
    if (shouldInterceptBuyNow) {
      document.body.addEventListener("click", handleWalletClick, {
        capture: true,
      });
    }

    return () => {
      document.body.removeEventListener("click", handleWalletClick, {
        capture: true,
      });
    };
  }, [shouldInterceptBuyNow, handleWalletClick]);

  // Inject custom CSS for Buy Now coverage
  useEffect(() => {
    if (shouldInterceptBuyNow && buyNowCustomCss) {
      const styleId = "redo-buy-now-custom-css";

      // Check if style element already exists
      let styleElement = document.getElementById(styleId) as HTMLStyleElement;

      if (!styleElement) {
        // Create new style element
        styleElement = document.createElement("style");
        styleElement.id = styleId;
        styleElement.type = "text/css";
        document.body.append(styleElement);
      }

      // Update the CSS content
      styleElement.textContent = buyNowCustomCss;

      // Cleanup function
      return () => {
        const element = document.getElementById(styleId);
        if (element) {
          element.remove();
        }
      };
    }

    return () => {};
  }, [shouldInterceptBuyNow, buyNowCustomCss]);
};
